import { useQuery } from '@tanstack/react-query';

import { bamsShortsApi } from 'api/bamsShorts';

import { BAMS_SHORTS_GC_TIME, BAMS_SHORTS_QUERY_KEY, BAMS_SHORTS_STALE_TIME } from './bamsShortsQueryKeys';

/**
 * Aggregate BAMS feed for the current + prior ISO pack week (brand x facility x pack date).
 *
 * Both weeks are fetched together because the week view compares them side by side.
 * A single week failing is tolerated; only a total failure throws. Returns the raw API
 * rows (mapping to the dashboard's internal shape stays in the component). Gated on the
 * server by the VIEW_BAMS_SHORTS auth role.
 *
 * @param {Object} params
 * @param {{week: number, year: number}} params.cur current ISO week
 * @param {{week: number, year: number}} params.prv prior ISO week
 * @param {boolean} [params.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<Object>, Error>}
 */
export const useBamsShortsFeedQuery = ({ cur, enabled = true, prv }) =>
    useQuery({
        queryKey: [BAMS_SHORTS_QUERY_KEY, 'feed', cur?.year, cur?.week, prv?.year, prv?.week],
        enabled: enabled && !!cur && !!prv,
        staleTime: BAMS_SHORTS_STALE_TIME,
        gcTime: BAMS_SHORTS_GC_TIME,
        queryFn: async () => {
            const [f1, f2] = await Promise.all([
                bamsShortsApi.getFeed(cur.week, cur.year),
                bamsShortsApi.getFeed(prv.week, prv.year),
            ]);
            if (f1?.status === false && f2?.status === false) {
                throw new Error(f1?.msg || f2?.msg || 'Could not load the feed.');
            }
            return [].concat(f1?.data?.rows || [], f2?.data?.rows || []);
        },
    });
